import { useState } from 'react';
import { useJobs, useCompletedJobs, useWeekPayment, useRecordJob } from '../../api/jobs';
import {
  getSaturdayWeekStart,
  getWeekDates,
  toISODate,
  formatPence,
  formatWeekRange,
  calculateBonus,
} from '../../utils/jobsWeek';
import type { Job, CompletedJob } from '@family/shared';

interface Props {
  childName: string;
  /** True when a parent is simulating the child's view. Jobs recorded here go in as unapproved. */
  isParentSimulating?: boolean;
}

export default function ChildJobsView({ childName }: Props) {
  const [weekOffset, setWeekOffset] = useState(0);

  const weekStart = getSaturdayWeekStart(new Date(), weekOffset);
  const weekStartStr = toISODate(weekStart);
  const isCurrentWeek = weekOffset === 0;
  const weekDates = getWeekDates(weekStart);

  const { data: jobsData, isLoading: jobsLoading } = useJobs();
  const { data: completedData, isLoading: completedLoading } = useCompletedJobs(childName, weekStartStr);
  const { data: paymentData } = useWeekPayment(childName, weekStartStr);
  const recordJob = useRecordJob();

  const jobs = jobsData?.data ?? [];
  const completed = completedData?.data ?? [];
  const payment = paymentData?.data ?? null;

  const completedByJobAndDate = new Map<string, CompletedJob>();
  for (const c of completed) {
    completedByJobAndDate.set(`${c.jobId}:${c.completedDate}`, c);
  }

  // Returns completions for a job this week, in chronological order (fills left-to-right)
  function getJobCompletions(jobId: string): CompletedJob[] {
    return weekDates
      .map(d => completedByJobAndDate.get(`${jobId}:${toISODate(d)}`))
      .filter((c): c is CompletedJob => c !== undefined);
  }

  const totalApprovedPence = jobs.reduce((sum, job) => {
    const approvedCount = getJobCompletions(job.id).filter(c => c.approved).length;
    return sum + approvedCount * job.rewardPence;
  }, 0);

  const bonusPence = calculateBonus(childName, totalApprovedPence);
  const totalWithBonus = totalApprovedPence + bonusPence;

  const isPastWeek = weekOffset < 0;
  const isPaid = payment?.paid ?? false;

  async function handleRecord(job: Job) {
    const today = toISODate(new Date());
    const alreadyDoneToday = completedByJobAndDate.has(`${job.id}:${today}`);
    if (alreadyDoneToday) return;

    try {
      await recordJob.mutateAsync({
        jobId: job.id,
        childName,
        completedDate: today,
        approved: false,
      });
    } catch (err: any) {
      if (err.message?.includes('already recorded')) return;
      console.error(err);
    }
  }

  if (jobsLoading || completedLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Week navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setWeekOffset(o => o - 1)}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
          aria-label="Previous week"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="text-center">
          <div className="font-semibold text-gray-900">
            {isCurrentWeek ? 'Current week' : formatWeekRange(weekStart)}
          </div>
          {!isCurrentWeek && (
            <button
              onClick={() => setWeekOffset(0)}
              className="text-xs text-blue-600 hover:underline mt-0.5"
            >
              Back to current week
            </button>
          )}
        </div>

        <button
          onClick={() => setWeekOffset(o => o + 1)}
          disabled={isCurrentWeek}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Next week"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Jobs table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left py-3 px-3 text-sm font-medium text-gray-600">Job</th>
              <th className="py-3 px-3 text-sm font-medium text-gray-600">This week</th>
              <th className="hidden sm:table-cell py-3 px-3 text-sm font-medium text-gray-600 text-right w-16">Reward</th>
              {isCurrentWeek && <th className="py-3 px-2 w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {jobs.map((job, idx) => {
              const todayStr = toISODate(new Date());
              const doneToday = completedByJobAndDate.has(`${job.id}:${todayStr}`);
              const canRecord = isCurrentWeek && !doneToday && !isPaid;

              const completions = getJobCompletions(job.id);
              const approvedCount = completions.filter(c => c.approved).length;
              const pendingCount = completions.filter(c => !c.approved).length;
              // Empty slots: shown on desktop only, hidden on mobile
              const emptyCount = 7 - completions.length;

              return (
                <tr
                  key={job.id}
                  className={`border-b border-gray-50 ${idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}
                >
                  {/* Job name + price subtext on mobile */}
                  <td className="py-2 px-3 text-sm text-gray-900">
                    <div className="leading-tight">{job.name}</div>
                    <div className="sm:hidden text-xs text-gray-400 mt-0.5">({formatPence(job.rewardPence)})</div>
                  </td>

                  {/* Bar chart: fills left-to-right. Grey empties hidden on mobile. */}
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1.5">
                      {Array.from({ length: approvedCount }, (_, i) => (
                        <div key={`a${i}`} className="w-5 h-5 rounded-sm bg-green-500 border border-green-600 flex-shrink-0" />
                      ))}
                      {Array.from({ length: pendingCount }, (_, i) => (
                        <div key={`p${i}`} className="w-5 h-5 rounded-sm bg-blue-400 border border-blue-500 flex-shrink-0" />
                      ))}
                      {Array.from({ length: emptyCount }, (_, i) => (
                        <div key={`e${i}`} className="hidden sm:block w-5 h-5 rounded-sm bg-gray-100 border border-gray-200 flex-shrink-0" />
                      ))}
                    </div>
                  </td>

                  {/* Reward: desktop only */}
                  <td className="hidden sm:table-cell py-2 px-3 text-sm text-gray-500 text-right">
                    {formatPence(job.rewardPence)}
                  </td>

                  {/* Record button: current week only */}
                  {isCurrentWeek && (
                    <td className="py-2 px-2">
                      <button
                        onClick={() => handleRecord(job)}
                        disabled={!canRecord || recordJob.isPending}
                        title={doneToday ? 'Already recorded today' : `Record ${job.name}`}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-lg transition-colors ${
                          canRecord
                            ? 'bg-blue-500 hover:bg-blue-600 cursor-pointer'
                            : 'bg-gray-200 cursor-not-allowed'
                        }`}
                      >
                        +
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer: total + payment status */}
      <div className="flex items-center justify-between px-1">
        <div className="text-sm text-gray-600">
          Total earned:{' '}
          <span className="font-semibold text-gray-900">{formatPence(totalWithBonus)}</span>
          {bonusPence > 0 && (
            <div className="text-xs text-green-600 mt-0.5">Includes {formatPence(bonusPence)} bonus</div>
          )}
        </div>

        {isPaid ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Paid
          </span>
        ) : isPastWeek && totalApprovedPence > 0 ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-700">
            Not paid yet
          </span>
        ) : null}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-400 px-1">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-green-500 inline-block" /> Approved
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-blue-400 inline-block" /> Waiting for approval
        </span>
      </div>
    </div>
  );
}
